import { ArrowUpRight } from "../components/CommonComponents";

const ProgramHighlights = ({ course }) => {
  const items = [
    {
      label: "Programme",
      value: course?.title || "Programme",
      detail: "Academic programme",
      featured: true,
    },
    {
      label: "Duration",
      value: course?.duration || "As prescribed",
      detail: "Programme duration",
    },
    {
      label: "Programme Level",
      value: course?.level || "University Programme",
      detail: "Academic level",
    },
    {
      label: "Learning Approach",
      value: "Theory + Application",
      detail: "Integrated learning",
    },
    {
      label: "Academic Experience",
      value: "Structured Learning",
      detail: "Progressive curriculum",
    },
    {
      label: "Career Orientation",
      value: "Future Focused",
      detail: "Professional development",
    },
  ];

  return (
    <section
      id="program-highlights"
      aria-labelledby="program-highlights-heading"
      className="relative overflow-hidden bg-navy py-20 text-white lg:py-28"
    >
      {/* Large background number */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -right-8 top-0 hidden font-serif text-[260px] leading-none tracking-[-0.08em] text-white/[0.025] xl:block"
      >
        01
      </span>

      {/* Decorative lines */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute left-0 top-[145px] hidden h-px w-[9%] bg-gold/60 lg:block"
      />

      <div className="container relative z-[1] mx-auto">

        {/* ===============================
            HEADER
        ================================ */}
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-end lg:gap-20">

          <div>
            <div className="flex items-center gap-3 text-sm font-bold uppercase tracking-[0.16em] text-gold">
              <span className="h-px w-10 bg-current" />
              <span>Program Highlights</span>
            </div>

            <h2
              id="program-highlights-heading"
              className="mt-6 max-w-[650px] font-serif text-[clamp(44px,5vw,72px)] font-normal leading-[1.03] tracking-[-0.04em]"
            >
              The programme,
              <br />

              <em className="font-normal italic text-gold">
                at a glance.
              </em>
            </h2>
          </div>

          <div className="lg:pb-2">
            <p className="max-w-[610px] text-lg leading-[1.8] text-white/65">
              A concise view of the academic structure and learning experience
              that define{" "}
              <strong className="font-medium text-white">
                {course?.title || "this programme"}
              </strong>{" "}
              at ISBM University.
            </p>
          </div>
        </div>

        {/* Gold rule */}
        <div className="my-12 flex items-center lg:my-16">
          <span className="h-[2px] w-[90px] bg-gold" />
          <span className="h-px flex-1 bg-white/[0.14]" />
        </div>

        {/* ===============================
            PROGRAMME DOSSIER
        ================================ */}
        <div className="bg-white text-navy shadow-[0_30px_80px_rgba(0,0,0,0.2)]">

          {/* Dossier header */}
          <div className="flex flex-col gap-6 border-b border-[#E2DDD3] px-7 py-7 md:flex-row md:items-center md:justify-between md:px-10 lg:px-12">
            <div>
              <span className="text-sm font-bold uppercase tracking-[0.16em] text-burgundy">
                Programme Profile
              </span>

              <p className="mt-2 text-base text-ink/55">
                Essential programme information
              </p>
            </div>

            <div className="flex items-center gap-3">
              <span className="h-2.5 w-2.5 rounded-full bg-gold" />

              <span className="text-sm font-bold uppercase tracking-[0.12em] text-navy">
                ISBM University
              </span>
            </div>
          </div>

          {/* Items */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3">
            {items.map((item, index) => (
              <article
                key={`${item.label}-${index}`}
                className={`
                  group
                  relative
                  min-h-[220px]
                  overflow-hidden
                  border-[#E2DDD3]
                  px-7 py-8
                  transition-colors
                  duration-300
                  hover:bg-ivory
                  md:px-9
                  md:py-9
                  lg:min-h-[245px]
                  lg:px-10
                  lg:py-10

                  ${index % 3 !== 2
                    ? "lg:border-r"
                    : ""
                  }

                  ${index < 3
                    ? "lg:border-b"
                    : ""
                  }

                  ${index % 2 === 0
                    ? "md:border-r lg:border-r"
                    : "md:border-r-0"
                  }

                  ${index < 4
                    ? "md:border-b"
                    : ""
                  }

                  ${item.featured
                    ? "bg-ivory"
                    : "bg-white"
                  }
                `}
              >
                {/* top index */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold uppercase tracking-[0.14em] text-burgundy">
                    {item.label}
                  </span>

                  <span className="font-serif text-[22px] text-gold">
                    0{index + 1}
                  </span>
                </div>

                {/* value */}
                <div className="mt-10">
                  <strong
                    className={`
                      block
                      font-serif
                      font-normal
                      leading-[1.18]
                      tracking-[-0.02em]
                      text-navy

                      ${item.featured
                        ? "text-[28px] lg:text-[31px]"
                        : "text-[27px]"
                      }
                    `}
                  >
                    {item.value}
                  </strong>

                  <span className="mt-4 block text-base text-ink/50">
                    {item.detail}
                  </span>
                </div>

                {/* bottom hover bar */}
                <span
                  aria-hidden="true"
                  className="absolute bottom-0 left-0 h-[3px] w-0 bg-burgundy transition-all duration-500 group-hover:w-full"
                />
              </article>
            ))}
          </div>

          {/* Dossier footer */}
          <div className="grid border-t border-[#E2DDD3] lg:grid-cols-[1fr_auto] lg:items-center">
            <div className="px-7 py-7 md:px-10 lg:px-12">
              <p className="max-w-[720px] text-base leading-[1.7] text-ink/60">
                Continue to the eligibility section to review the academic
                requirements for admission to{" "}
                <strong className="font-semibold text-navy">
                  {course?.title || "this programme"}.
                </strong>
              </p>
            </div>

            <a
              href="#eligibility"
              className="group flex min-h-[76px] items-center justify-between gap-12 bg-burgundy px-7 text-sm font-bold text-white transition-colors duration-300 hover:bg-burgundy-dark md:px-9 lg:min-h-[92px]"
            >
              Check Eligibility

              <ArrowUpRight className="h-[19px] w-[19px] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
          </div>
        </div>

        {/* ===============================
            BOTTOM MICRO MESSAGE
        ================================ */}
        <div className="mt-8 flex flex-col gap-4 border-t border-white/[0.12] pt-7 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-sm uppercase tracking-[0.14em] text-white/40">
            Academic Journey
          </span>

          <div className="flex flex-wrap items-center gap-3 text-sm font-semibold text-white/70">
            <span>Learn</span>
            <span className="h-1 w-1 rounded-full bg-gold" />
            <span>Apply</span>
            <span className="h-1 w-1 rounded-full bg-gold" />
            <span>Develop</span>
            <span className="h-1 w-1 rounded-full bg-gold" />
            <span>Progress</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProgramHighlights;